import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PregnancyCurve } from "@/components/PregnancyCurve";
import {
  ArrowLeft, ClipboardList, FileCheck2, Download, ExternalLink,
  AlertTriangle, Baby, Plus, Weight, Ruler, LayoutGrid, UserRound, BookOpen, MessagesSquare, Printer,
} from "lucide-react";
import { usePatientConsents, ConsentsSummary, ConsentsEditor } from "@/components/PatientConsents";
import { DermaPhotoGallery } from "@/components/DermaPhotoGallery";
import { useAuth } from "@/context/AuthContext";
import { Camera } from "lucide-react";

const STATUS_LABELS = {
  aperto: "Aperto", in_gestione: "In gestione", inviato_specialista: "Inviato allo specialista",
  in_attesa: "In attesa", risposta_ricevuta: "Risposta ricevuta", chiuso: "Chiuso",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return "—"; }
};

function TimelineItem({ ev, idx, total }) {
  const navigate = useNavigate();
  const isCase = ev.kind === "case";
  const accent = isCase ? "border-blue-300 bg-blue-50/50" : "border-emerald-300 bg-emerald-50/50";
  const dot = isCase ? "bg-blue-600" : "bg-emerald-600";
  return (
    <li className="relative pl-8" data-testid={`timeline-item-${idx}`}>
      <span className={`absolute left-[7px] top-4 h-3.5 w-3.5 rounded-full ring-4 ring-white ${dot}`} />
      <Card className={`p-4 rounded-xl border shadow-sm ${accent}`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-white text-slate-700 border-slate-200">#{total - idx}</Badge>
              <span className="font-semibold text-slate-900">{fmtDate(ev.at)}</span>
              <Badge className={isCase ? "bg-blue-100 text-blue-800 border-blue-200" : "bg-emerald-100 text-emerald-800 border-emerald-200"}>
                {isCase ? <ClipboardList className="h-3 w-3 mr-1" /> : <FileCheck2 className="h-3 w-3 mr-1" />}
                {ev.title}
              </Badge>
              {ev.pregnancy_week && (
                <Badge className="bg-pink-100 text-pink-800 border-pink-200" data-testid={`timeline-pregnancy-${idx}`}>
                  <Baby className="h-3 w-3 mr-1" /> Settimana {ev.pregnancy_week}
                </Badge>
              )}
              {ev.has_red_flags && (
                <Badge className="bg-red-100 text-red-800 border-red-200">
                  <AlertTriangle className="h-3 w-3 mr-1" /> Red flag
                </Badge>
              )}
              {isCase && ev.status && (
                <span className="text-xs text-slate-500">{STATUS_LABELS[ev.status] || ev.status}</span>
              )}
            </div>
            {ev.reason && <div className="mt-2 text-sm text-slate-700"><span className="text-slate-500">Motivo:</span> {ev.reason}</div>}
            {ev.summary && <div className="mt-1 text-sm text-slate-600 italic line-clamp-3">{ev.summary}</div>}
            {isCase && (ev.final_hypothesis || ev.hypotheses?.length > 0) && (
              <div className="mt-1 text-xs text-slate-700">
                <span className="text-slate-500">Ipotesi:</span>{" "}
                {ev.final_hypothesis || ev.hypotheses.join(", ")}
              </div>
            )}
            {!isCase && (ev.weight_kg || ev.height_cm || ev.diet_type) && (
              <div className="mt-2 flex gap-3 text-xs text-slate-600 flex-wrap">
                {ev.weight_kg && <span className="flex items-center gap-1"><Weight className="h-3.5 w-3.5" /> {ev.weight_kg} kg</span>}
                {ev.height_cm && <span className="flex items-center gap-1"><Ruler className="h-3.5 w-3.5" /> {ev.height_cm} cm</span>}
                {ev.diet_type && <span className="text-purple-700 font-medium">Dieta: {ev.diet_type}</span>}
              </div>
            )}
            {isCase && ev.follow_ups_count > 0 && (
              <div className="mt-1 text-xs text-slate-500">{ev.follow_ups_count} follow-up/esiti registrati</div>
            )}
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {isCase ? (
              <Button size="sm" variant="outline" onClick={() => navigate(ev.link)}
                      data-testid={`timeline-open-${idx}`} className="h-9 bg-white">
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Apri caso
              </Button>
            ) : ev.download_url ? (
              <a href={ev.download_url} target="_blank" rel="noreferrer" data-testid={`timeline-download-${idx}`}>
                <Button size="sm" variant="outline" className="h-9 bg-white">
                  <Download className="h-3.5 w-3.5 mr-1.5" /> PDF
                </Button>
              </a>
            ) : null}
          </div>
        </div>
      </Card>
    </li>
  );
}

const TABS = [
  { k: "bacheca", l: "Bacheca", icon: LayoutGrid },
  { k: "anagrafica", l: "Anagrafica", icon: UserRound },
  { k: "diario", l: "Diario", icon: BookOpen },
  { k: "foto", l: "Foto", icon: Camera, derma: true },
  { k: "comunicazioni", l: "Comunicaz.", icon: MessagesSquare },
  { k: "documenti", l: "Doc & Stampe", icon: Printer },
];

const Section = ({ title, children, testid }) => (
  <Card className="p-4 md:p-5 rounded-lg border-slate-200 shadow-sm bg-white" data-testid={testid}>
    <h3 className="text-[15px] font-medium text-slate-600 mb-3">{title}</h3>
    {children}
  </Card>
);

const Field = ({ label, value }) => (
  <div className="flex items-baseline gap-3 py-1.5 border-b border-slate-100">
    <span className="text-[11px] text-slate-400 w-28 flex-shrink-0 text-right">{label}</span>
    <span className="text-sm text-slate-800">{value || "—"}</span>
  </div>
);

const calcAge = (iso) => { if (!iso) return ""; const d = new Date(iso); const n = new Date();
  let a = n.getFullYear() - d.getFullYear(); if (n < new Date(n.getFullYear(), d.getMonth(), d.getDate())) a--; return a; };

export default function CartellaPazientePage() {
  const { cf } = useParams();
  const [sp] = useSearchParams();
  const email = sp.get("email") || "";
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(sp.get("tab") || "bacheca");
  const [threads, setThreads] = useState([]);
  const [photoCount, setPhotoCount] = useState(0);
  const isDerma = /dermatolog/i.test(user?.specialization || "");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = cf && cf !== "_" ? { fiscal_code: cf } : { email };
        const { data } = await api.get("/patients/timeline", { params });
        setData(data);
      } catch { toast.error("Impossibile caricare la cartella paziente"); }
      finally { setLoading(false); }
    })();
    api.get("/threads").then((r) => setThreads(r.data || [])).catch(() => setThreads([]));
  }, [cf, email]);

  const p = data?.patient || {};
  const items = data?.items || [];
  const fullName = `${p.patient_first_name || ""} ${p.patient_last_name || ""}`.trim() || "Paziente";
  const fiscal = p.patient_fiscal_code || (cf !== "_" ? cf : "");
  const { consents, save } = usePatientConsents(fiscal);
  useEffect(() => {
    if (!fiscal || fiscal.length !== 16) { setPhotoCount(0); return; }
    api.get("/derma-photos", { params: { patient_fiscal_code: fiscal } })
      .then((r) => setPhotoCount((r.data.items || []).length)).catch(() => setPhotoCount(0));
  }, [fiscal]);
  const visibleTabs = TABS.filter((t) => !t.derma || isDerma || photoCount > 0);
  // Il parametro ?tab= viene validato contro i tab visibili (un non-dermatologo non può aprire la galleria via URL)
  const activeTab = visibleTabs.some((t) => t.k === tab) ? tab : "bacheca";
  const initials = `${(p.patient_first_name || "?")[0]}${(p.patient_last_name || "")[0] || ""}`.toUpperCase();
  const isPregnant = items.some((ev) => ev.pregnancy_week);
  const patientThreads = threads.filter((t) =>
    (fiscal && (t.patient_fiscal_code || "").toUpperCase() === fiscal) ||
    (p.patient_email && (t.patient_email || "").toLowerCase() === p.patient_email.toLowerCase()) ||
    (fullName !== "Paziente" && (t.patient_name || "").toLowerCase() === fullName.toLowerCase()));
  const docs = items.filter((ev) => ev.kind === "report" && ev.download_url);

  return (
    <div className="min-h-full bg-[#EEF1F5]" data-testid="cartella-paziente-page">
      <div className="max-w-5xl mx-auto px-4 py-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <button onClick={() => navigate("/cartelle-pazienti")} data-testid="cartella-paziente-back"
                  className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> Cartelle pazienti
          </button>
          <Button onClick={() => navigate(`/nuovo-referto?cf=${encodeURIComponent(fiscal || "")}`)}
                  data-testid="cartella-paziente-new-visit"
                  className="h-9 bg-[#1E88E5] hover:bg-[#1976D2] text-white">
            <Plus className="h-4 w-4 mr-2" /> Nuova visita
          </Button>
        </div>

        {/* Testata paziente */}
        <Card className="p-4 rounded-lg border-slate-200 shadow-sm bg-white">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="h-20 w-20 rounded-md bg-slate-200 text-slate-600 flex items-center justify-center font-heading font-bold text-2xl flex-shrink-0"
                 data-testid="cartella-paziente-avatar">{initials}</div>
            <div className="flex-1 min-w-[220px]">
              <h1 className="font-heading text-xl md:text-2xl font-bold text-slate-900" data-testid="cartella-paziente-name">
                {loading ? "Caricamento…" : fullName}
              </h1>
              <div className="text-sm text-slate-600 mt-0.5">
                {p.patient_birth_date && <div>{fmtDate(p.patient_birth_date)} · {calcAge(p.patient_birth_date)} anni{p.patient_sex ? ` · ${p.patient_sex === "F" ? "Femmina" : p.patient_sex === "M" ? "Maschio" : p.patient_sex}` : ""}</div>}
                {(p.patient_email || p.patient_phone) && <div>{[p.patient_email, p.patient_phone].filter(Boolean).join(" · ")}</div>}
                <div className="text-xs mt-1"><span className="font-semibold text-slate-800">Paziente attivo</span>{fiscal && <> · CF: <span className="font-mono">{fiscal}</span></>}</div>
              </div>
            </div>
            <div className="min-w-[230px]"><ConsentsSummary consents={consents} /></div>
            {data && (
              <div className="flex flex-col gap-1 items-end" data-testid="cartella-paziente-counters">
                <Badge className="bg-[#1E88E5]/10 text-[#1565C0] border-[#1E88E5]/20">{data.count} visite</Badge>
                <Badge className="bg-slate-100 text-slate-700 border-slate-200">{data.cases_count} casi · {data.reports_count} referti</Badge>
              </div>
            )}
          </div>
        </Card>

        {/* Tab */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm flex overflow-x-auto" data-testid="cartella-tabs">
          {visibleTabs.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`cartella-tab-${t.k}`}
                    className={`flex flex-col items-center gap-1 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-wide border-b-2 transition-colors ${
                      activeTab === t.k ? "bg-[#1E88E5] text-white border-[#1E88E5]" : "text-slate-600 border-transparent hover:bg-slate-50"}`}>
              <t.icon className="h-4 w-4" /> {t.l}{t.k === "foto" && photoCount > 0 ? ` (${photoCount})` : ""}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center text-slate-500 py-10">Caricamento…</div>
        ) : (
          <div className="space-y-3">
            {activeTab === "bacheca" && (<>
              <Section title="Riepilogo" testid="cartella-bacheca">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div className="rounded-md bg-slate-50 p-3"><div className="text-[11px] text-slate-400">Ultima visita</div><div className="font-semibold text-slate-800">{items[0] ? `${fmtDate(items[0].at)} · ${items[0].title}` : "—"}</div></div>
                  <div className="rounded-md bg-slate-50 p-3"><div className="text-[11px] text-slate-400">Motivo</div><div className="text-slate-800">{items[0]?.reason || "—"}</div></div>
                  <div className="rounded-md bg-slate-50 p-3"><div className="text-[11px] text-slate-400">Conclusioni</div><div className="text-slate-800 line-clamp-3">{items[0]?.summary || "—"}</div></div>
                </div>
              </Section>
              {isPregnant && fiscal && <PregnancyCurve fiscalCode={fiscal} patientName={fullName} />}
            </>)}

            {activeTab === "anagrafica" && (<>
              <Section title="Anagrafica Paziente" testid="cartella-anagrafica">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                  <Field label="Nome" value={p.patient_first_name} />
                  <Field label="Cognome" value={p.patient_last_name} />
                  <Field label="C. Fiscale" value={fiscal} />
                  <Field label="Data di nascita" value={p.patient_birth_date ? fmtDate(p.patient_birth_date) : ""} />
                  <Field label="Sesso" value={p.patient_sex === "F" ? "Femmina" : p.patient_sex === "M" ? "Maschio" : p.patient_sex} />
                  <Field label="Età" value={p.patient_birth_date ? `${calcAge(p.patient_birth_date)} anni` : ""} />
                </div>
              </Section>
              <Section title="Recapiti">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                  <Field label="Email" value={p.patient_email} />
                  <Field label="Telefono" value={p.patient_phone} />
                </div>
              </Section>
              <Section title="Consensi e alert" testid="cartella-consensi">
                <ConsentsEditor consents={consents} onSave={save} />
              </Section>
            </>)}

            {activeTab === "diario" && (
              items.length === 0 ? (
                <Section title="Diario clinico"><div className="text-sm text-slate-500">Nessuna visita registrata.</div></Section>
              ) : (
                <ul className="relative space-y-3 before:absolute before:left-[13px] before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200"
                    data-testid="cartella-paziente-timeline">
                  {items.map((ev, idx) => <TimelineItem key={`${ev.kind}-${ev.id}`} ev={ev} idx={idx} total={items.length} />)}
                </ul>
              )
            )}

            {activeTab === "foto" && (
              <Section title="Documentazione fotografica dermatologica" testid="cartella-foto">
                <DermaPhotoGallery fiscalCode={fiscal} />
              </Section>
            )}

            {activeTab === "comunicazioni" && (
              <Section title="Comunicazioni relative al paziente" testid="cartella-comunicazioni">
                {patientThreads.length === 0 ? (
                  <div className="text-sm text-slate-500">Nessuna comunicazione collegata a questo paziente.</div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {patientThreads.map((t) => (
                      <li key={t.id}>
                        <button onClick={() => navigate(`/messaggi/${t.id}`)} className="w-full text-left py-2.5 flex items-center justify-between gap-3 hover:bg-slate-50 rounded px-1"
                                data-testid={`cartella-thread-${t.id}`}>
                          <div className="min-w-0"><div className="text-sm font-semibold text-slate-800 truncate">{t.subject || "(Senza oggetto)"}</div>
                            <div className="text-xs text-slate-500">{t.request_status || ""}{t.priority && t.priority !== "normale" ? ` · priorità ${t.priority}` : ""}</div></div>
                          <span className="text-[11px] text-slate-400 flex-shrink-0">{t.last_message_at ? fmtDate(t.last_message_at) : ""}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            )}

            {activeTab === "documenti" && (
              <Section title="Documenti e stampe" testid="cartella-documenti">
                {docs.length === 0 ? (
                  <div className="text-sm text-slate-500">Nessun referto PDF disponibile.</div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {docs.map((d) => (
                      <li key={d.id} className="py-2.5 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0"><FileCheck2 className="h-4 w-4 text-[#1E88E5] flex-shrink-0" />
                          <div className="min-w-0"><div className="text-sm font-semibold text-slate-800 truncate">{d.title}</div><div className="text-xs text-slate-500">{fmtDate(d.at)}</div></div></div>
                        <a href={d.download_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-8 bg-white"><Download className="h-3.5 w-3.5 mr-1" /> PDF</Button></a>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
